/* eslint no-console: 0 */
import { OutlinedInput } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import { makeStyles } from '@material-ui/core/styles';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle.ts';
import { useGetDevice } from '../../../hooks/useGetDevice.ts';
import { useUpdateDeviceConfigMutation } from '../../../hooks/useUpdateDeviceConfigMutation';
import { BATTERY_MODE_OPTIONS, BatteryModeId, DEFAULT_TRACKING_MODE_ID } from '../../../models/device-config';
import { descriptionForBatteryMode, durationDescriptionForBatteryMode, extraInfoForBatteryMode } from '../../../services/device-config-utils';
import screenNavigator from '../../../services/ScreenNavigator';
import Button from '../../bits/Button.jsx';
import Loading from '../Loading.jsx';
import { Alert } from '@mui/material';

const useStyles = makeStyles({
  selectRoot: {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#4a8bcc', // Focused border color
    },
  },
  formControl: {
    marginTop: '24px',
  },
});

interface EditBatteryModeRouteProps { }

const BatteryModeDescription = styled.div`
  margin-top: 24px;
`;

/**
 * Component that allows editing device name.
 */
export const EditBatteryModeRoute: React.FC<EditBatteryModeRouteProps> = () => {
  const { id: deviceId } = useParams();
  const { data: device, isLoading } = useGetDevice({ deviceId });
  const [batteryMode, setBatteryMode] = useState<BatteryModeId>(DEFAULT_TRACKING_MODE_ID);
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const mutation = useUpdateDeviceConfigMutation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const classes = useStyles();

  useSetModalTitle('Battery Mode');
  
  useEffect(() => {
    const batteryMode = device?.latestDeviceConfig?.data.batteryMode || DEFAULT_TRACKING_MODE_ID;
    setBatteryMode(batteryMode);
  }, [device?.latestDeviceConfig]);

  const onBatteryModeChange = useCallback(
    (e: React.ChangeEvent<{ name?: string; value: any }>) => {
      setBatteryMode(e.target.value)
      setHasChanged(true);

    }, [setBatteryMode],
  );

  useEffect(() => {
    const displayUpdateWarning = ["fixed-interval-1hr", "fixed-interval-6hr", "fixed-interval-12hr"].includes(device?.batteryMode || '');
    if (displayUpdateWarning) {
      setInfoMessage(`Your device is currently in power-saving mode. Changes may take a few hours to take effect.`);
    }
  }, [device]);

  const onUpdate = useCallback(() => {
    const config = { batteryMode };

    mutation.mutate({ deviceId, config }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['useGetDevice', deviceId] });
        queryClient.setQueryData(['useGetDevice', deviceId], null);
        screenNavigator.exitCurrentScreen(history);
      },
      onError: () => {
        console.error('Failed to update battery mode');
      },
    });
  }, [mutation, batteryMode, deviceId, queryClient, history]);

  return (
    <>
      {isLoading && <Loading />}

      {!isLoading && device && <>

        {infoMessage && <Alert severity="info">{infoMessage}</Alert> }

        <FormControl variant="outlined" fullWidth className={classes.formControl}>
          <InputLabel htmlFor="outlined-model-native-simple">Mode</InputLabel>
          <Select
            fullWidth={true}
            native
            value={batteryMode}
            onChange={onBatteryModeChange}
            label="Mode"
            input={
              <OutlinedInput
                label="Mode"
                id="outlined-model-native-simple"
                classes={{ root: classes.selectRoot }}
              />
            }
          >
            {BATTERY_MODE_OPTIONS.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
          </Select>
        </FormControl>

        <BatteryModeDescription>{descriptionForBatteryMode(batteryMode)}</BatteryModeDescription>

        <BatteryModeDescription>{extraInfoForBatteryMode(batteryMode)}</BatteryModeDescription>

        <BatteryModeDescription>Estimated battery life: <strong>{durationDescriptionForBatteryMode(batteryMode)}</strong></BatteryModeDescription>

        <div style={{ marginTop: '30px' }}>
          <Button
            onClick={onUpdate}
            color="primary"
            disabled={!hasChanged || mutation.isLoading}
            loading={mutation.isLoading}>Update</Button>
        </div>
      </>}
    </>
  );
};
